import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom/client';

import {assert, assertAndContinue} from 'Asserts';
import BufferTabView from 'buffers/BufferTabView';
import {CutPosition, MatchSite} from 'constants/SequenceConstants';
import AaSequenceConstants from 'molbio/aa-sequences/util/AaSequenceConstants';
import CodonOptimizationTab from 'molbio/codon-optimization/CodonOptimizationTab';
import {CodonOptimizationTabsSelector} from 'molbio/codon-optimization/reduceAllCodonOptimizationTabs';
import Cutsite from 'molbio/enzymes/backbone/CutSite';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import * as sequtil from 'molbio/SeqUtil';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';
import {getStore} from 'redux-stores/Store';

import CodonOptimizationTabState from './CodonOptimizationTabState';

class CodonOptimizationTabView extends BufferTabView<CodonOptimizationTabState> {
  reactRoot: ReactDOM.Root | null = null;

  override initialize(): void {
    this.listenTo(this.model.polymer().selection(), 'change', () => this.render());
    if (this.model.isSequenceTabState()) {
      const sequence = this.model.sequence();
      this.listenTo(sequence.get('cutsites'), 'change', () => this.render());
    }
  }

  override render(): Backbone.View<CodonOptimizationTabState> {
    const selection = this.model.polymer().selection();
    const selector = new CodonOptimizationTabsSelector(getStore().getState());
    const state = selector.getState(this.model.cid);
    assert(state != null, 'Expected tab state to exist');
    const strand = state.strand!;
    if (this.model.isSequenceTabState()) {
      assert(
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        state.optimizationStart !== null && state.optimizationEnd !== null,
        'Expected optimizationStart and optimizationEnd to be set'
      );
      const sequence = this.model.sequence();
      const optimizationStart = state.optimizationStart;
      const optimizationEnd = state.optimizationEnd;
      const seqLength = sequence.length();
      this.reactRoot = renderToDOM(
        <CodonOptimizationTab
          onClose={() => {
            this.model.close();
          }}
          name={sequence.name()}
          tabId={this.model.cid}
          onReplaceSequence={(
            start: number,
            end: number,
            newBases: string,
            options?: {callback: () => void}
          ): void => {
            sequence.exactReplaceBases(start, end, newBases, options);
          }}
          startIndex={sequence.get('_sequence').get('startIndex')}
          onSetSelection={(start: number, end: number): void => {
            selection.setEndpoints(start, end);
          }}
          currentSelection={{start: selection.start(), end: selection.end()}}
          seqLength={sequence.length()}
          sequenceId={sequence.id}
          circular={sequence.circular()}
          isBackTranslation={false}
          isRegionEditable={sequence.isRegionEditable(optimizationStart, optimizationEnd)}
          strand={strand}
          seqEnd={sequence.get('_sequence').get('length')}
          matchSites={sequence
            .get('enzymes')
            .map((enzyme: Enzyme) =>
              sequence
                .get('_sequence')
                .getCuts(enzyme.toJSON())
                .map((cut: CutPosition) => new Cutsite({enzyme, position: cut.position, strand: cut.strand}))
            )
            .flat()
            .map((cutsite: Cutsite) => sequtil.getMatchSiteInfo(cutsite, seqLength))
            .filter((matchSite: MatchSite) =>
              sequtil.isRangeOverlap(
                matchSite.matchStart,
                matchSite.matchEnd,
                optimizationStart,
                optimizationEnd
              )
            )}
          createFromSelection={(bases: string, name: string) =>
            sequence.createFromSelection(optimizationStart, optimizationEnd, {
              annotations: true,
              notcontained: true,
              tags: false,
              reverseComplement: false,
              bases,
              name,
            })
          }
        />,
        this.el,
        this.reactRoot
      );
    } else if (this.model.isAaSequenceTabState()) {
      const aaSequence = this.model.protein();
      this.reactRoot = renderToDOM(
        <CodonOptimizationTab
          onClose={() => {
            this.model.close();
          }}
          name={aaSequence.name()}
          tabId={this.model.cid}
          onReplaceSequence={null}
          startIndex={AaSequenceConstants.DEFAULT_START_INDEX}
          onSetSelection={(start: number, end: number): void => {
            selection.setEndpoints(start, end);
          }}
          currentSelection={{start: selection.start(), end: selection.end()}}
          seqLength={aaSequence.length()}
          sequenceId={aaSequence.id}
          circular={false}
          isBackTranslation={true}
          isRegionEditable={false}
          strand={strand}
          seqEnd={null}
          matchSites={null}
          createFromSelection={null}
        />,
        this.el,
        this.reactRoot
      );
    } else {
      assertAndContinue(false, 'Unexpected codon optimization input type.', {polymer: this.model.polymer()});
    }
    return this;
  }

  override removeAsync(callback: () => void): this {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }
}

CodonOptimizationTabView.prototype.className = 'buffer-tab';

export default CodonOptimizationTabView;
